import { animate, state, style, transition, trigger } from '@angular/animations';
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { MatDrawer } from '@angular/material/sidenav';
import { PERMISSION_NAME, PermissionService } from '@ebw-monorepo/utils';
import { Feature } from 'ol';
import { Subscription } from 'rxjs';
import { IMapConfig, IMapLayout } from '../../models/config-mappa.model';
import { FeatureActionEvent } from '../../models/feature-action-event';
import { DrawingService } from '../../services/drawing.service';
import { FeatureSelectService } from '../../services/feature-select.service';
import { GoogleStreetViewService } from '../../services/google-street-view.service';
import { MapService } from '../../services/map.service';
import { PsaService } from '../../services/psa.service';
import { SidebarService } from '../../services/sidebar.service';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'map-component',
  templateUrl: './map-component.component.html',
  styleUrls: ['./map-component.component.scss'],
  animations: [
    trigger('openControlsMenu', [
      state(
        'closed',
        style({
          height: '0px',
          opacity: '0',
          display: 'none'
        })
      ),
      state(
        'open',
        style({
          height: 'auto',
          opacity: '1',
          display: 'flex'
        })
      ),
      transition('closed => open', animate('100ms ease-in')),
      transition('open => closed', animate('100ms ease-out'))
    ])
  ]
})
export class MapComponent implements AfterViewInit, OnDestroy {
  @Output() changeActivePreviewHandler: EventEmitter<{
    indexChange: number;
    action: any;
    features: Feature[];
  }> = new EventEmitter<{ indexChange: number; action: any; features: Feature[] }>();
  @ViewChild('sidebarDrawer') sidebarDrawer: MatDrawer;

  drawingToolOpen = false;

  streetviewActiveSubscription: Subscription = null;

  expandedControls = false;

  streetviewPanel: google.maps.StreetViewPanorama = null;

  subscriptions: Subscription[] = [];

  canViewPlot: boolean = false;

  canViewDXF: boolean = false;

  canViewPrint: boolean = false;

  @ViewChild('mapComponentFrame') mapFrame: ElementRef;
  mapFrameSizeObserver: ResizeObserver = null;

  /** Default Map Layout of the visible components  */
  mapLayout: IMapLayout = {
    worldLayerChange: { active: true },
    mapPreview: { active: true },
    infoView: { active: true },
    googleSearch: { active: true },
    googleStreetView: { active: true },
    zoomControls: { active: false },
    legend: { active: true },
    goToPositionControl: { active: true },
    drawingTool: { active: true },
    featureSelect: { active: true },
    fullScreenControl: { active: false },
    layerSelection: { active: true },
    transformCoordinates: { active: true },
    dynamic_layers: { active: true },
    shape_layers: { active: true },
    query: { active: true },
    bookmark: { active: true },
    valves: { active: true },
    otdr: { active: true },
    checkInOut: { active: true },
    analisiSchiacciamenti: { active: true },
    psa: { active: true },
    tracing: { active: true },
    checkInAnalysis: { active: true },
    fiberJoints: { active: true },
    toolFattibilita: { active: true }
  };

  /**
   * Map configuration input. REQUIRED
   */
  private _mapConfiguration: IMapConfig;
  public get mapConfiguration(): IMapConfig {
    return this._mapConfiguration;
  }
  @Input() set mapConfiguration(val: IMapConfig) {
    this._mapConfiguration = val;
    this.mapService.mapConfigurationLoaded$.next(val);
    //this.styleService.mapStyles = this._mapConfiguration.styles;
  }

  @Input() externalToolbar = false;

  /** MapTableHandler */
  constructor(
    public mapService: MapService,
    public streetviewService: GoogleStreetViewService,
    public sidebarService: SidebarService,
    public drawingToolService: DrawingService,
    public psaService: PsaService,
    public featureSelectService: FeatureSelectService,
    private _permissionService: PermissionService
  ) {}

  ngAfterViewInit(): void {
    // check if the user can see the plotting tool
    this.canViewPlot = this._permissionService.checkPermission(PERMISSION_NAME.MAP.PLOTTING);

    this.canViewDXF = this._permissionService.checkPermission(PERMISSION_NAME.MAP.EXPORT_DXF);

    this.canViewPrint = this._permissionService.checkPermission(PERMISSION_NAME.MAP.PRINT);

    //Resize Open Layers map after the view has started
    this.mapService.resizeOpenLayersMap();

    //Listen to changes in the size of the OpenLayers container and resize the map when it changes.
    this.mapFrameSizeObserver = new ResizeObserver(() => {
      this.mapService.resizeOpenLayersMap();
    });
    this.mapFrameSizeObserver.observe(this.mapFrame.nativeElement);
    this.sidebarService.sidebarDrawer = this.sidebarDrawer;

    if (this.mapConfiguration && this.mapConfiguration.mapLayout) {
      setTimeout(() => {
        this.mapLayout = this.mapConfiguration.mapLayout;
      }, 0);
    }
  }

  /** When the component is destroyed, unsubscribe from all the subscriptions, and disconnect the Size Observer. */
  ngOnDestroy(): void {
    this.drawingToolService.closeDrawingTool();
    this.streetviewActiveSubscription?.unsubscribe();
    if (this.mapFrameSizeObserver) this.mapFrameSizeObserver.disconnect();
    this.subscriptions.forEach((s) => {
      s.unsubscribe();
    });
  }

  /** Function to toggle the expansion of the extra controls (button with 3 dots) */
  toggleControls() {
    this.expandedControls = !this.expandedControls;
  }

  managerConfirm(evt) {
    //this.layersConfig.confirm();
  }

  openLayerSelection() {
    this.mapService.clearAllFeaturesFromTemporaryLayer(false);
    this.sidebarService.sidebarOpened = 1;
    this.sidebarDrawer.open();
  }

  // /** Open the transform coordinates component on the sidebar */
  // openTransformCoordinates() {
  //   this.mapService.clearAllFeaturesFromTemporaryLayer(false);
  //   this.sidebarService.sidebarOpened = 2;
  //   this.sidebarDrawer.open();
  // }

  /** Open the Drawing Tool component. */
  openDrawingTool() {
    //Resize the map before opening to avoid a bug where the overlays where disaligned.
    this.mapService.resizeOpenLayersMap();
    this.drawingToolService.openDrawingTool();
  }

  /** Open the legend component on the sidebar */
  openLegend() {
    this.mapService.clearAllFeaturesFromTemporaryLayer(false);
    this.sidebarService.sidebarOpened = 4;
    this.sidebarDrawer.open();
  }

  /** Open the Dynamic Layers component on the sidebar for adding layers from external source. */
  openDynamcLayers() {
    this.mapService.clearAllFeaturesFromTemporaryLayer(false);
    this.sidebarService.sidebarOpened = 5;
    this.sidebarDrawer.open();
  }

  /** Close the sidebar and the component that was rendered inside it. */
  closeSidebar() {
    this.mapService.clearAllFeaturesFromTemporaryLayer(false);
    this.sidebarService.sidebarOpened = null;
    this.sidebarDrawer.close();
  }

  // NEW
  onBackButtonClick() {
    this.mapService.showBackButton = false;
    this.mapService.backButtonEventSubject.next(true);
  }

  moveToLonLatZoom(lon: number, lat: number, zoom?: number) {
    this.mapService.setMapCenter([lon, lat]);
    if (zoom != null) {
      this.mapService.mapZoom = zoom;
    }
  }

  previewHandler($event: FeatureActionEvent) {
    this.changeActivePreviewHandler.emit($event);
  }

  openLegendLayers() {}
}
